import { memo, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';
import { TFunction } from 'i18next';

import { projectStatusByCode } from 'entities';

import { Column, Row, Loader, Tabs, Tag } from 'components/Blocks';
import { ListManipulation } from 'components/ListManipulation';
import { SortFilterButtons } from 'components/Buttons';
import { ButtonGroup } from 'components/ButtonGroup';
import { KanbanBoxIcon, ListCollapseIcon, TableIcon } from 'components/Icons';

import { ActiveProjects } from './ActiveProjects';
import { ProjectByStatus } from './ProjectsByStatus';
import { SearchProject } from './Search';

import { useProject } from 'providers/Project';

import { useUrlStateV2 } from 'hooks';

import { evaluateSearchTerm } from './utils';

import { getSorters } from './sorters';
import { getProjectFilters } from './filters';
import { appStorage } from 'utils/storage';

export type ProjectFilter = Record<
  'status' | 'owners' | 'taskOwners' | 'tasks' | 'salesPerson' | 'tags',
  string[]
>;

export type ProjectTab = 'inProgress' | 'Completed' | 'Canceled' | 'Unassigned';

export interface ActiveTabProjectCountControl {
  length: number;
  loading: boolean;
}

const tabs = [
  { value: 'inProgress', getLabel: (t: TFunction) => t('in-progress') },
  { value: 'Unassigned', getLabel: (t: TFunction) => t('unassigned') },
  { value: 'Completed', getLabel: (t: TFunction) => t('completed') },
  { value: 'Canceled', getLabel: (t: TFunction) => t('canceled') },
];

const ActiveProjectsMemo = memo(ActiveProjects);

const ProjectByStatusMemo = memo(ProjectByStatus);

export function ProjectList() {
  const { loadingProjects, projectList } = useProject();
  const { t } = useLang();

  const [query, updateQuery] = useUrlStateV2({
    tab: {
      defaultValue: 'inProgress',
      validate: (value) => !!tabs.find((t) => t.value === value),
    },

    view: {
      defaultValue: appStorage.session.get<string>('_h__V') ?? 'cards',
      onValue: (tab) => appStorage.session.set('_h__V', tab),
      validate: (value) => ['cards', 'table', 'kanban'].includes(value),
    },

    search: '',
  });

  const [activeTabProjectCount, setActiveTabProjectCount] = useState(
    {} as ActiveTabProjectCountControl,
  );

  const filteredProjects = useMemo(() => {
    const baseProjects = projectList.filter(
      (project) =>
        projectStatusByCode[project.status]?.shownOnActivePage &&
        evaluateSearchTerm(project, query.search || ''),
    );

    return baseProjects;
  }, [projectList, query.search]);

  const handleTabChange = (newTab: string): void => {
    if (newTab !== 'inProgress') setActiveTabProjectCount({ loading: true, length: 0 });

    updateQuery({ tab: newTab });
  };

  const manipulationsProps = useMemo(
    () => ({
      filters: getProjectFilters(),
      sorters: getSorters(),
    }),
    [],
  );

  const isLoading = query.tab === 'inProgress' ? loadingProjects : activeTabProjectCount.loading;

  return (
    <ListManipulation itens={filteredProjects} storageKey="p-list-f@" {...manipulationsProps}>
      {({ hasFilter, hasSorter, openFilters, openSorters, parsedItens, activeFilters }) => (
        <Column gap="4">
          <Column>
            <Row gap="3">
              <h5 className="text-3xl font-medium text-primary">{t('projects')}</h5>

              <Tag
                color="#edf8ff"
                className="px-3 py-1 font-medium text-primary dark:bg-muted"
                name={
                  isLoading ? (
                    <Loader type="dualRing" size={14} />
                  ) : query.tab === 'inProgress' ? (
                    parsedItens.length
                  ) : (
                    activeTabProjectCount.length
                  )
                }
              />
            </Row>

            <Row justifyContent="space-between">
              <Tabs
                onTabChange={handleTabChange}
                value={query.tab}
                tabs={tabs}
                responsiveBreakpoint="MID_TABLET"
              />

              {!loadingProjects && query.tab === 'inProgress' && (
                <Row gap="2">
                  <SearchProject
                    term={query.search}
                    updateTerm={(search) => updateQuery({ search })}
                  />

                  <SortFilterButtons
                    gap="1"
                    hasFilter={hasFilter}
                    hasSort={hasSorter}
                    onFilterClick={openFilters}
                    onSortClick={openSorters}
                  />

                  <ButtonGroup
                    value={query.view}
                    onChange={(view) => updateQuery({ view })}
                    options={[
                      { value: 'cards', label: <ListCollapseIcon /> },
                      { value: 'table', label: <TableIcon /> },
                      { value: 'kanban', label: <KanbanBoxIcon /> },
                    ]}
                  />
                </Row>
              )}
            </Row>
          </Column>

          {query.tab === 'inProgress' ? (
            <ActiveProjectsMemo
              view={query.view}
              projects={parsedItens}
              filters={activeFilters}
              loading={isLoading}
            />
          ) : (
            <ProjectByStatusMemo
              key={query.tab}
              tab={query.tab as ProjectTab}
              setActiveTabProjectCount={setActiveTabProjectCount}
            />
          )}
        </Column>
      )}
    </ListManipulation>
  );
}
